/**
 * Auth API Service
 * Handles communication with hyperamm-api auth endpoints
 * JWT is stored in HttpOnly cookie by the server
 */

import { isAddress } from "viem";
import { apiFetch } from "./fetch";

export interface AuthResult {
  success: boolean;
  expiresAt: string;
  isNewUser: boolean;
}

export interface AuthError {
  error: string;
  message: string;
}

/**
 * Get a nonce for SIWE message from the API
 * Uses credentials: include for iron-session cookie
 */
export async function getNonce(): Promise<string> {
  const response = await apiFetch("/api/auth/nonce", {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as AuthError).message || "Failed to get nonce");
  }

  const data = await response.json();
  return data.nonce;
}

/**
 * Verify SIWE signature and authenticate/register user
 * JWT is set as HttpOnly cookie by the server
 */
export async function verify(
  message: string,
  signature: string,
  accessCode?: string,
  referralCode?: string,
): Promise<AuthResult> {
  const response = await apiFetch("/api/auth/verify", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      message,
      signature,
      ...(accessCode && { accessCode }),
      ...(referralCode && { referralCode }),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error((errorData as AuthError).message || "Verification failed");
    (error as Error & { code?: string }).code = (errorData as AuthError).error;
    throw error;
  }

  return response.json();
}

/**
 * Refresh JWT token
 * New token is set as HttpOnly cookie by the server
 */
export async function refresh(): Promise<AuthResult> {
  const response = await apiFetch("/api/auth/refresh", {
    method: "POST",
    credentials: "include",
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as AuthError).message || "Failed to refresh token");
  }

  return response.json();
}

/**
 * Logout - clears the auth cookie on the server
 * Throws if server fails to clear the cookie
 */
export async function logoutApi(): Promise<void> {
  const response = await apiFetch("/api/auth/logout", {
    method: "POST",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to logout from server");
  }
}

/**
 * Check if a wallet address is already registered
 */
export async function checkWalletRegistered(address: string): Promise<boolean> {
  if (!isAddress(address)) {
    return false;
  }

  const response = await apiFetch(`/api/auth/check/${address}`, {
    method: "GET",
  });

  if (!response.ok) {
    return false;
  }

  const data = await response.json();
  return data.registered;
}

/**
 * Auth error codes from the API
 */
export const AUTH_ERROR_CODES = {
  ACCESS_CODE_REQUIRED: "ACCESS_CODE_REQUIRED",
  INVALID_OR_USED_CODE: "INVALID_OR_USED_CODE",
  WALLET_ALREADY_REGISTERED: "WALLET_ALREADY_REGISTERED",
  INVALID_SIGNATURE: "INVALID_SIGNATURE",
  INVALID_NONCE: "INVALID_NONCE",
  SESSION_EXPIRED: "SESSION_EXPIRED",
} as const;
